/**
 * ConversationService - CRUD operations for conversations
 *
 * Handles creating conversations, sending messages, marking as read,
 * archiving/muting, finding existing 1:1s, and group management.
 *
 * @module services/messaging/ConversationService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  createConversationDocument,
  createMessageDocument,
  createSystemMessage,
  createLastMessageSummary,
  CONVERSATION_TYPES,
  CONVERSATION_STATUS,
  MESSAGE_STATUS
} from '../../models/Conversation';

class ConversationService {
  /**
   * Find existing 1:1 conversation between two users
   * Prevents creating duplicate DMs
   *
   * @param {string} userId1
   * @param {string} userId2
   * @returns {Promise<Object|null>} Existing conversation or null
   */
  async findExisting1to1(userId1, userId2) {
    try {
      // Query conversations where userId1 is a participant
      const q = query(
        collection(db, 'conversations'),
        where('participantIds', 'array-contains', userId1),
        where('type', '==', CONVERSATION_TYPES.DIRECT)
      );

      const snapshot = await getDocs(q);

      // Check which ones also have userId2
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.participantIds?.includes(userId2) && data.participantIds.length === 2) {
          return { id: docSnap.id, ...data };
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding existing 1:1:', error);
      return null;
    }
  }

  /**
   * Create new conversation
   *
   * @param {Object} params
   * @param {string} params.schoolId - School ID
   * @param {string} params.type - Conversation type
   * @param {string} params.subject - Brief subject/description
   * @param {string|null} params.studentId - Student ID (if student-centric)
   * @param {string|null} params.studentName - Student name (if student-centric)
   * @param {Array} params.participants - Array of participant objects
   * @param {string} params.createdBy - User ID of creator
   * @param {string|null} params.initialMessage - Optional first message text
   * @returns {Promise<string>} conversationId
   */
  async createConversation({
    schoolId,
    type,
    subject,
    studentId = null,
    studentName = null,
    participants,
    createdBy,
    initialMessage = null,
    relatedMentionId = null,
    relatedConsequenceId = null
  }) {
    try {
      // For 1:1 conversations, check if one already exists
      if (type === CONVERSATION_TYPES.DIRECT && participants.length === 2) {
        const existing = await this.findExisting1to1(
          participants[0].userId,
          participants[1].userId
        );
        if (existing) {
          console.log(`Reusing existing 1:1 conversation: ${existing.id}`);
          return existing.id;
        }
      }

      // Block parent-initiated DMs when the school has set messaging to one-way.
      // Existing threads stay open (sendMessage is unaffected); only new thread
      // creation is gated. Firestore rules enforce this server-side too.
      if (type === CONVERSATION_TYPES.DIRECT && schoolId) {
        const creator = participants.find(p => p.userId === createdBy);
        if (creator?.role === 'parent') {
          const schoolSnap = await getDoc(doc(db, 'schools', schoolId));
          if (schoolSnap.exists() && schoolSnap.data().parentDmsDisabled === true) {
            const err = new Error('Your school has set messaging to one-way. You can reply to messages from staff, but cannot start new conversations.');
            err.code = 'parent-dms-disabled';
            throw err;
          }
        }
      }

      // Create conversation document
      const conversationData = createConversationDocument({
        schoolId,
        type,
        subject,
        studentId,
        studentName,
        participants,
        createdBy,
        relatedMentionId,
        relatedConsequenceId
      });

      // Add to Firestore
      const conversationRef = await addDoc(
        collection(db, 'conversations'),
        conversationData
      );

      // Send initial message if provided
      if (initialMessage) {
        const creator = participants.find(p => p.userId === createdBy);
        await this.sendMessage(conversationRef.id, {
          senderId: createdBy,
          senderName: creator?.name || 'Unknown',
          senderRole: creator?.role || 'teacher',
          text: initialMessage
        });
      } else {
        // Send system message
        await this._addSystemMessage(
          conversationRef.id,
          `Conversation started by ${participants.find(p => p.userId === createdBy)?.name || 'Unknown'}`
        );
      }

      return conversationRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Send message in conversation
   *
   * @param {string} conversationId
   * @param {Object} messageData
   * @returns {Promise<string>} messageId
   */
  async sendMessage(conversationId, messageData) {
    try {
      const { senderId, senderName, senderRole, text, replyTo = null, media = null, type } = messageData;

      // Validate conversation exists
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversation = conversationSnap.data();

      // Check participant using participantIds (fast) or fallback to participants array
      const isParticipant = conversation.participantIds
        ? conversation.participantIds.includes(senderId)
        : conversation.participants?.some(p => p.userId === senderId && p.active);

      if (!isParticipant) {
        throw new Error('User is not a participant in this conversation');
      }

      // Create message document
      const messageParams = { senderId, senderName, senderRole, text, replyTo, media };
      if (type) messageParams.type = type;
      const message = createMessageDocument(messageParams);

      // Mark as sent (will be confirmed by Firestore)
      message.status = MESSAGE_STATUS.SENT;

      // Add message to subcollection
      const messageRef = await addDoc(
        collection(db, 'conversations', conversationId, 'messages'),
        message
      );

      // Update conversation document
      const batch = writeBatch(db);

      // Update lastMessage and lastMessageAt
      batch.update(conversationRef, {
        lastMessage: createLastMessageSummary(senderId, senderName, text),
        lastMessageAt: serverTimestamp(),
        messageCount: increment(1)
      });

      // Increment unread counts for other participants
      const participantIds = conversation.participantIds || conversation.participants?.map(p => p.userId) || [];
      const unreadUpdates = {};
      participantIds.forEach(pid => {
        if (pid !== senderId) {
          unreadUpdates[`unreadCounts.${pid}`] = increment(1);
        }
      });

      if (Object.keys(unreadUpdates).length > 0) {
        batch.update(conversationRef, unreadUpdates);
      }

      await batch.commit();

      return messageRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Edit a message (own messages only)
   *
   * @param {string} conversationId
   * @param {string} messageId
   * @param {string} newText
   */
  async editMessage(conversationId, messageId, newText) {
    try {
      const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        text: newText,
        editedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  /**
   * Delete message for current user only
   *
   * @param {string} conversationId
   * @param {string} messageId
   * @param {string} userId
   */
  async deleteMessageForMe(conversationId, messageId, userId) {
    try {
      const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        deletedFor: arrayUnion(userId)
      });
    } catch (error) {
      console.error('Error deleting message for me:', error);
      throw error;
    }
  }

  /**
   * Delete message for everyone (sender only)
   *
   * @param {string} conversationId
   * @param {string} messageId
   */
  async deleteMessageForEveryone(conversationId, messageId) {
    try {
      const messageRef = doc(db, 'conversations', conversationId, 'messages', messageId);
      await updateDoc(messageRef, {
        deletedForEveryone: true,
        text: ''
      });
    } catch (error) {
      console.error('Error deleting message for everyone:', error);
      throw error;
    }
  }

  /**
   * Mark conversation as read for user
   */
  async markAsRead(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCounts.${userId}`]: 0,
        [`lastReadTimestamps.${userId}`]: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      throw error;
    }
  }

  /**
   * Archive conversation for user
   */
  async archiveConversation(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        archivedBy: arrayUnion(userId)
      });
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  }

  /**
   * Unarchive conversation for user
   */
  async unarchiveConversation(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        archivedBy: arrayRemove(userId)
      });
    } catch (error) {
      console.error('Error unarchiving conversation:', error);
      throw error;
    }
  }

  /**
   * Mute conversation for user
   */
  async muteConversation(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        mutedBy: arrayUnion(userId)
      });
    } catch (error) {
      console.error('Error muting conversation:', error);
      throw error;
    }
  }

  /**
   * Unmute conversation for user
   */
  async unmuteConversation(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        mutedBy: arrayRemove(userId)
      });
    } catch (error) {
      console.error('Error unmuting conversation:', error);
      throw error;
    }
  }

  /**
   * Pin/unpin conversation for user
   */
  async togglePinConversation(conversationId, userId, pinned) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        pinnedBy: pinned ? arrayUnion(userId) : arrayRemove(userId)
      });
    } catch (error) {
      console.error('Error toggling pin:', error);
      throw error;
    }
  }

  /**
   * Update typing status
   */
  async setTyping(conversationId, userId, isTyping) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      if (isTyping) {
        await updateDoc(conversationRef, {
          [`typing.${userId}`]: serverTimestamp()
        });
      } else {
        await updateDoc(conversationRef, {
          [`typing.${userId}`]: null
        });
      }
    } catch (error) {
      // Don't throw for typing updates - non-critical
      console.warn('Error updating typing status:', error);
    }
  }

  // ============================================================================
  // GROUP MANAGEMENT
  // ============================================================================

  /**
   * Add participant to a group conversation
   */
  async addParticipant(conversationId, userId, userName = 'Unknown', role = 'teacher') {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const participant = { userId, name: userName, role, joinedAt: serverTimestamp(), active: true };

      await updateDoc(conversationRef, {
        participants: arrayUnion(participant),
        participantIds: arrayUnion(userId),
        [`unreadCounts.${userId}`]: 0,
      });

      await this._addSystemMessage(conversationId, `${userName} was added`);
    } catch (error) {
      console.error('Error adding participant:', error);
      throw error;
    }
  }

  /**
   * Remove participant from a group conversation (admin only)
   */
  async removeParticipant(conversationId, userId, removedByUserId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      if (!conversationSnap.exists()) throw new Error('Conversation not found');

      const data = conversationSnap.data();
      const participant = data.participants?.find(p => p.userId === userId);
      const removedName = participant?.name || 'Unknown';

      // Remove from participants array (need to find exact object)
      const updatedParticipants = (data.participants || []).filter(p => p.userId !== userId);

      await updateDoc(conversationRef, {
        participants: updatedParticipants,
        participantIds: arrayRemove(userId),
        admins: arrayRemove(userId)
      });

      await this._addSystemMessage(conversationId, `${removedName} was removed`);
    } catch (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  }

  /**
   * Leave a group conversation
   */
  async leaveGroup(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      if (!conversationSnap.exists()) throw new Error('Conversation not found');

      const data = conversationSnap.data();
      const participant = data.participants?.find(p => p.userId === userId);
      const leavingName = participant?.name || 'Unknown';

      const updatedParticipants = (data.participants || []).filter(p => p.userId !== userId);

      await updateDoc(conversationRef, {
        participants: updatedParticipants,
        participantIds: arrayRemove(userId),
        admins: arrayRemove(userId)
      });

      await this._addSystemMessage(conversationId, `${leavingName} left`);
    } catch (error) {
      console.error('Error leaving group:', error);
      throw error;
    }
  }

  /**
   * Toggle admin status for a participant
   */
  async toggleAdmin(conversationId, userId, isAdmin) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        admins: isAdmin ? arrayUnion(userId) : arrayRemove(userId)
      });
    } catch (error) {
      console.error('Error toggling admin:', error);
      throw error;
    }
  }

  /**
   * Update group info (name, avatar, description)
   */
  async updateGroupInfo(conversationId, updates) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const allowed = {};
      if (updates.groupName !== undefined) allowed.groupName = updates.groupName;
      if (updates.groupAvatar !== undefined) allowed.groupAvatar = updates.groupAvatar;
      if (updates.groupDescription !== undefined) allowed.groupDescription = updates.groupDescription;
      if (updates.groupName !== undefined) allowed.subject = updates.groupName; // Keep subject in sync

      await updateDoc(conversationRef, allowed);

      if (updates.groupName) {
        await this._addSystemMessage(conversationId, `Group name changed to "${updates.groupName}"`);
      }
    } catch (error) {
      console.error('Error updating group info:', error);
      throw error;
    }
  }

  /**
   * Get single conversation by ID
   */
  async getConversationById(conversationId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        return null;
      }

      return { id: conversationSnap.id, ...conversationSnap.data() };
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }
  }

  /**
   * Add system message to conversation (internal use)
   * @private
   */
  async _addSystemMessage(conversationId, text) {
    try {
      const message = createSystemMessage(text);
      await addDoc(
        collection(db, 'conversations', conversationId, 'messages'),
        message
      );
    } catch (error) {
      console.error('Error adding system message:', error);
      // Non-critical - don't throw
    }
  }
}

// Export singleton instance
export default new ConversationService();
